import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import saveSurveyResult from '../utils/saveSurveyResult'
import createSurveyFaceRecordingManager from '../utils/createSurveyFaceRecordingManager'
import './genZMkSurvey.css'

const SURVEY_CODE = 'gen_z_xk_neuro'
const ADS = [
  { id: 'sunny', name: 'Reklama 1', url: 'https://ai.surveyneuro.com/images/5-Sunny.mp4' },
  { id: 'ipko', name: 'Reklama 2', url: 'https://ai.surveyneuro.com/images/6-IPKO.mp4' },
]
const POINTS = [
  [10, 25], [50, 25], [90, 25],
  [10, 55], [50, 55], [90, 55],
  [10, 85], [50, 85], [90, 85],
]
const CLICKS_PER_POINT = 5
const IMPLICIT_DEADLINE_MS = 1800
const IMPLICIT_ATTRIBUTES = [
  { label: 'Autentik', words: ['Autentik', 'I sinqertë', 'I vërtetë'] },
  { label: 'Në modë', words: ['Në modë', 'Bashkëkohor', 'Inovativ'] },
  { label: 'I besueshëm', words: ['I besueshëm', 'I denjë për besim', 'I sigurt'] },
]
const PLATFORMS = ['TikTok', 'Instagram', 'YouTube', 'Snapchat', 'Twitch', 'X']
const TOPICS = ['Meme', 'Udhëzues/bëje vetë', 'Videolojëra', 'Fitnes', 'Modë/bukuri', 'Teknologji', 'Lajme', 'Politikë/aktivizëm', 'Ushqim']
const SOURCES = ['Miqtë', 'Influencuesit', 'Kanalet e markave', 'Gazetarët/Mediat', 'Faqet me vlerësime të produkteve dhe shërbimeve']
const AD_QUESTIONS = [
  'Kjo reklamë më tërhoqi.',
  'Mesazhi ishte i qartë.',
  'Reklama m’u duk e sinqertë/autentike.',
  'Kjo reklamë më ndikoi emocionalisht.',
  'Kjo reklamë ma rriti besimin te marka.',
  'Pas shikimit të reklamës, ka më shumë gjasa që ta blej/provoj produktin ose shërbimin e reklamuar.',
]
const BELIEF_QUESTIONS = [
  'Qëndrueshmëria ndikon në vendimet e mia për blerje.',
  'Preferoj markat që mbajnë qëndrim për çështjet shoqërore.',
  'Rekomandimet e influencuesve janë të besueshme.',
  'Më shqetëson mënyra se si markat i përdorin të dhënat e mia.',
]
const choice = (value, choices) => choices.includes(value)
const shuffle = (items) => [...items].sort(() => Math.random() - 0.5)
const makeResponseId = () => `xk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
const average = (values) => values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : null

function summarizeImplicit(trials) {
  const valid = trials.filter((trial) => !trial.timed_out)
  return {
    trial_count: trials.length,
    on_time_count: valid.length,
    mean_on_time_rt_ms: average(valid.map((trial) => trial.rt_ms)),
    attributes: Object.fromEntries(IMPLICIT_ATTRIBUTES.map((attribute) => {
      const group = trials.filter((trial) => trial.attribute === attribute.label)
      const onTime = group.filter((trial) => !trial.timed_out)
      return [attribute.label, {
        match_count: group.filter((trial) => trial.response === 'matches').length,
        trial_count: group.length,
        mean_on_time_rt_ms: average(onTime.map((trial) => trial.rt_ms)),
      }]
    })),
  }
}

function Scale({ questions, answers, onChange, prefix }) {
  return <div className="mk-matrix">
    <p className="mk-help">1 = Nuk pajtohem aspak · 7 = Pajtohem plotësisht</p>
    {questions.map((question, index) => <fieldset key={question} className="mk-fieldset">
      <legend>{prefix}{questions.length === 1 ? '' : index + 1}. {question}</legend>
      <div className="mk-scale">{[1, 2, 3, 4, 5, 6, 7].map((number) => <label key={number} className={Number(answers[index]) === number ? 'selected' : ''}><input type="radio" name={`${prefix}-${index}`} checked={Number(answers[index]) === number} onChange={() => onChange(index, number)} />{number}</label>)}</div>
    </fieldset>)}
  </div>
}

export default function GenZXkSurvey() {
  const responseId = useMemo(() => new URLSearchParams(window.location.search).get('responseId') || makeResponseId(), [])
  const adOrder = useMemo(() => shuffle(ADS), [])
  const faceRecording = useMemo(() => createSurveyFaceRecordingManager({ surveyCode: SURVEY_CODE, responseId }), [responseId])
  const implicitTrialSets = useMemo(() => adOrder.map(() => shuffle(IMPLICIT_ATTRIBUTES.flatMap((attribute, attributeIndex) => attribute.words.map((word) => ({ attribute: attribute.label, attribute_index: attributeIndex + 1, stimulus: word }))))), [adOrder])
  const [phase, setPhase] = useState('consent')
  const [consent, setConsent] = useState({ age: '', camera: '', privacy: '' })
  const [demographics, setDemographics] = useState({ country: 'XK', age: '', gender: '', population: '', status: '' })
  const [habits, setHabits] = useState({ minutes: {}, shortVideos: '', topics: [], socialPurchase: '', onlinePercent: 50 })
  const [adIndex, setAdIndex] = useState(0)
  const [videoStarted, setVideoStarted] = useState(false)
  const [adAnswers, setAdAnswers] = useState({ scales: {}, share: '', impression: '' })
  const [adResults, setAdResults] = useState([])
  const [implicitIndex, setImplicitIndex] = useState(0)
  const [implicitResults, setImplicitResults] = useState([])
  const [implicitLate, setImplicitLate] = useState(false)
  const [beliefs, setBeliefs] = useState({ scales: {}, ranking: SOURCES })
  const [error, setError] = useState('')
  const [cameraState, setCameraState] = useState('idle')
  const [faceSeen, setFaceSeen] = useState(false)
  const [calibrationCounts, setCalibrationCounts] = useState(POINTS.map(() => 0))
  const [recalibration, setRecalibration] = useState(false)
  const trackerRef = useRef(null)
  const startedRef = useRef(false)
  const samplesRef = useRef([])
  const exposureRef = useRef(null)
  const exposuresRef = useRef([])
  const needsRecalibrationRef = useRef(false)
  const calibrationRef = useRef([])
  const videoRef = useRef(null)
  const implicitStartRef = useRef(0)
  const implicitLockedRef = useRef(false)
  const implicitTimerRef = useRef(null)
  const currentAd = adOrder[adIndex]
  const currentImplicitTrials = implicitTrialSets[adIndex]

  const stopTracker = useCallback(() => {
    faceRecording.abort()
    if (!startedRef.current) return
    try { trackerRef.current?.clearGazeListener() } catch (e) { console.warn(e) }
    try { trackerRef.current?.stopVideo() } catch (e) { console.warn(e) }
    try { trackerRef.current?.end() } catch (e) { console.warn(e) }
    startedRef.current = false
  }, [faceRecording])
  useEffect(() => () => stopTracker(), [stopTracker])
  useEffect(() => () => window.clearTimeout(implicitTimerRef.current), [])

  useEffect(() => {
    if (phase !== 'neutral') return undefined
    const timer = window.setTimeout(() => setPhase('ad-intro'), 3000)
    return () => window.clearTimeout(timer)
  }, [phase])

  useEffect(() => {
    if (phase !== 'implicit-stimulus') return undefined
    implicitLockedRef.current = false
    implicitStartRef.current = performance.now()
    setImplicitLate(false)
    const deadline = window.setTimeout(() => setImplicitLate(true), IMPLICIT_DEADLINE_MS)
    return () => window.clearTimeout(deadline)
  }, [phase, implicitIndex])

  const beginCamera = async (isRecalibration = false) => {
    setError('')
    setRecalibration(isRecalibration)
    setCalibrationCounts(POINTS.map(() => 0))
    setCameraState('loading')
    setPhase('camera-check')
    try {
      const module = await import('../webgazer/webgazer')
      const tracker = module.default
      trackerRef.current = tracker
      if (!tracker.detectCompatibility()) throw new Error('unsupported camera')
      if (!startedRef.current) {
        tracker.saveDataAcrossSessions(false).setRegression('ridge').setTracker('TFFacemesh').applyKalmanFilter(true).showVideoPreview(true).showPredictionPoints(false).setGazeListener((data) => {
          const face = tracker.getTracker()?.getPositions?.()
          setFaceSeen(Array.isArray(face) && face.length > 0)
          const exposure = exposureRef.current
          if (!exposure || !data || !Number.isFinite(data.x) || !Number.isFinite(data.y) || samplesRef.current.length >= 5000) return
          samplesRef.current.push({ x: Math.round(data.x * 100) / 100, y: Math.round(data.y * 100) / 100, t_ms: Math.round(performance.now() - exposure.performanceStart) })
        })
        await tracker.clearData()
        startedRef.current = true
        await tracker.begin()
        tracker.removeMouseEventListeners()
      } else {
        await tracker.resume()
      }
      tracker.setVideoViewerSize(300, 225)
      tracker.showVideoPreview(true)
      setCameraState('ready')
    } catch (e) {
      stopTracker()
      setCameraState('error')
      setError('Kamera ose gjurmimi i shikimit nuk mund të aktivizohet. Kontrolloni lejen e kamerës dhe provoni përsëri.')
    }
  }

  const startCalibration = () => {
    trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
    setPhase('calibration')
  }

  const clickPoint = (index, event) => {
    if (!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT) return
    const box = event.currentTarget.getBoundingClientRect()
    trackerRef.current?.recordScreenPosition(box.left + box.width / 2, box.top + box.height / 2, 'click')
    const next = [...calibrationCounts]
    next[index] += 1
    setCalibrationCounts(next)
    if (next.every((count) => count >= CLICKS_PER_POINT)) {
      calibrationRef.current.push({ completed_at: new Date().toISOString(), points: POINTS.length, clicks_per_point: CLICKS_PER_POINT, after_ad: recalibration ? adIndex : null })
      trackerRef.current?.showVideoPreview(false).showPredictionPoints(false)
      setCameraState('done')
      setPhase(recalibration ? 'neutral' : 'demographics')
    }
  }

  const validateConsent = () => {
    if (Object.values(consent).some((answer) => !answer)) return setError('Përgjigjuni të gjitha pyetjeve të detyrueshme.')
    if (Object.values(consent).some((answer) => answer === 'no')) return setPhase('ineligible')
    beginCamera()
  }
  const validateDemographics = () => {
    if (!demographics.age || !demographics.gender || !demographics.population || !demographics.status) return setError('Plotësoni të gjitha pyetjet.')
    const age = Number(demographics.age)
    if (!Number.isInteger(age) || age < 18 || age > 29) return setError('Ky studim është për persona nga 18 deri në 29 vjeç.')
    setError(''); setPhase('habits')
  }
  const validateHabits = () => {
    if (PLATFORMS.some((platform) => habits.minutes[platform] === undefined || habits.minutes[platform] === '') || !habits.shortVideos || !habits.socialPurchase) return setError('Plotësoni të gjitha pyetjet e detyrueshme.')
    if (PLATFORMS.some((platform) => Number(habits.minutes[platform]) < 0 || Number(habits.minutes[platform]) > 1440)) return setError('Shënoni nga 0 deri në 1440 minuta për secilën platformë.')
    setError(''); setPhase('ad-intro')
  }
  const beginAd = async () => {
    setError('')
    try {
      await trackerRef.current?.resume?.()
      faceRecording.prepare()
      setVideoStarted(false)
      setPhase('ad')
    } catch (e) { setError('Gjurmimi i shikimit nuk mund të vazhdojë. Provoni përsëri.') }
  }
  const onAdPlay = () => {
    if (exposureRef.current) return
    try { faceRecording.start() } catch (recordingError) {
      videoRef.current?.pause()
      faceRecording.abort()
      setError('Regjistrimi me kamerë nuk mund të fillojë. Kontrolloni lejen e kamerës dhe provoni përsëri.')
      setPhase('ad-intro')
      return
    }
    setVideoStarted(true)
    samplesRef.current = []
    exposureRef.current = { advertisement_id: currentAd.id, advertisement_url: currentAd.url, started_at: new Date().toISOString(), performanceStart: performance.now(), viewport: { width: window.innerWidth, height: window.innerHeight } }
  }
  const finishAd = async () => {
    const exposure = exposureRef.current
    if (!exposure) return
    exposureRef.current = null
    const durationMs = Math.round(performance.now() - exposure.performanceStart)
    needsRecalibrationRef.current = samplesRef.current.length < Math.max(10, Math.ceil(durationMs / 1000) * 2)
    const exposureResult = { advertisement_id: exposure.advertisement_id, advertisement_number: adIndex + 1, advertisement_url: exposure.advertisement_url, started_at: exposure.started_at, ended_at: new Date().toISOString(), duration_ms: durationMs, viewport: exposure.viewport, gaze_samples: samplesRef.current }
    faceRecording.finish(exposureResult)
    trackerRef.current?.pause?.()
    setPhase('recording-saving')
    try {
      await faceRecording.ensureUploaded([exposureResult])
      exposuresRef.current.push(exposureResult)
      setPhase('implicit-intro')
    } catch (recordingError) {
      setError('Regjistrimi i kamerës nuk u ruajt. Kontrolloni lidhjen dhe luajeni reklamën përsëri.')
      setPhase('ad-intro')
    }
  }
  const showImplicitTrial = () => {
    setPhase('implicit-fixation')
    implicitTimerRef.current = window.setTimeout(() => setPhase('implicit-stimulus'), 300)
  }
  const beginImplicitTest = () => {
    setImplicitIndex(0)
    setImplicitResults([])
    showImplicitTrial()
  }
  const respondImplicit = (response) => {
    if (phase !== 'implicit-stimulus' || implicitLockedRef.current) return
    implicitLockedRef.current = true
    const rtMs = Math.round(performance.now() - implicitStartRef.current)
    setImplicitResults((previous) => [...previous, { ...currentImplicitTrials[implicitIndex], response, rt_ms: rtMs, timed_out: rtMs > IMPLICIT_DEADLINE_MS }])
    setPhase('implicit-gap')
    implicitTimerRef.current = window.setTimeout(() => {
      if (implicitIndex + 1 >= currentImplicitTrials.length) setPhase('ad-questions')
      else { setImplicitIndex(implicitIndex + 1); showImplicitTrial() }
    }, 300)
  }
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'KeyE') respondImplicit('does_not_match')
      if (event.code === 'KeyI') respondImplicit('matches')
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  })
  const saveAdQuestions = () => {
    if (Object.keys(adAnswers.scales).length !== AD_QUESTIONS.length || !choice(adAnswers.share, ['yes', 'no']) || !adAnswers.impression.trim()) return setError('Përgjigjuni të gjitha pyetjeve për reklamën.')
    setAdResults((previous) => [...previous, { advertisement_id: currentAd.id, advertisement_url: currentAd.url, answers: adAnswers, implicit_trials: implicitResults, implicit_summary: summarizeImplicit(implicitResults) }])
    setAdAnswers({ scales: {}, share: '', impression: '' }); setError('')
    if (adIndex + 1 < adOrder.length) {
      setAdIndex(adIndex + 1)
      if (needsRecalibrationRef.current) { needsRecalibrationRef.current = false; beginCamera(true) }
      else setPhase('neutral')
    }
    else setPhase('beliefs')
  }
  const moveRanking = (index, direction) => {
    const target = index + direction
    if (target < 0 || target >= beliefs.ranking.length) return
    const ranking = [...beliefs.ranking]
    ;[ranking[index], ranking[target]] = [ranking[target], ranking[index]]
    setBeliefs((previous) => ({ ...previous, ranking }))
  }
  const save = async () => {
    if (Object.keys(beliefs.scales).length !== BELIEF_QUESTIONS.length) return setError('Përgjigjuni të gjitha pohimeve.')
    setError(''); setPhase('saving')
    try {
      stopTracker()
      if (exposuresRef.current.length !== adOrder.length) throw new Error('Missing advertisement exposure')
      await faceRecording.ensureUploaded(exposuresRef.current)
      const answerLabels = {
        'Mosha': demographics.age,
        'Gjinia': demographics.gender,
        'Vendbanimi': demographics.population,
        'Statusi': demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`H1 ${platform} (minuta)`, habits.minutes[platform]])),
        'H2 Video të shkurtra': habits.shortVideos,
        'H3 Përmbajtjet': habits.topics,
        'H4 Blerje nga rrjetet sociale': habits.socialPurchase === 'yes' ? 'Po' : 'Jo',
        'H5 Blerjet në internet (%)': habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((question, index) => [`${ad.advertisement_id} A${index + 1} ${question}`, ad.answers.scales[index]]),
          [`${ad.advertisement_id} A7 Ndarja`, ad.answers.share === 'yes' ? 'Po' : 'Jo'],
          [`${ad.advertisement_id} A8 Përshtypja`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute) => [`${ad.advertisement_id} Testi i nënkuptuar: ${attribute.label} (përputhje)`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id} Testi i nënkuptuar: koha mesatare (ms)`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((question, index) => [`B${index + 1} ${question}`, beliefs.scales[index]])),
        'B5 Burimet sipas besimit': beliefs.ranking,
      }
      const answers = {
        AGE: Number(demographics.age), GENDER: demographics.gender, POPULATION: demographics.population, STATUS: demographics.status,
        ...Object.fromEntries(PLATFORMS.map((platform) => [`H1_${platform.toUpperCase()}`, Number(habits.minutes[platform])])),
        H2: Number(habits.shortVideos), H3: habits.topics, H4: habits.socialPurchase, H5: habits.onlinePercent,
        ...Object.fromEntries(adResults.flatMap((ad) => [
          ...AD_QUESTIONS.map((_, index) => [`${ad.advertisement_id.toUpperCase()}_A${index + 1}`, Number(ad.answers.scales[index])]),
          [`${ad.advertisement_id.toUpperCase()}_A7`, ad.answers.share],
          [`${ad.advertisement_id.toUpperCase()}_A8`, ad.answers.impression],
          ...IMPLICIT_ATTRIBUTES.map((attribute, index) => [`${ad.advertisement_id.toUpperCase()}_IMP${index + 1}`, ad.implicit_trials.filter((trial) => trial.attribute === attribute.label && trial.response === 'matches').length]),
          [`${ad.advertisement_id.toUpperCase()}_RT`, ad.implicit_summary.mean_on_time_rt_ms],
        ])),
        ...Object.fromEntries(BELIEF_QUESTIONS.map((_, index) => [`B${index + 1}`, Number(beliefs.scales[index])])),
        B5: beliefs.ranking.join(' > '),
      }
      await saveSurveyResult({ surveyCode: SURVEY_CODE, responseId, result: {
        survey_code: SURVEY_CODE, response_id: responseId, status: 'completed', language: 'sq', country: 'XK', completed_at: new Date().toISOString(),
        consent: { age_18_or_over: true, camera_tracking: true, camera_video_storage: true, privacy_policy: true }, demographics, media_habits: habits,
        advertisement_order: adOrder.map((ad) => ad.id), advertisement_tests: adResults, beliefs, answers, answer_labels: answerLabels,
        eye_tracking: { engine: 'webgazer', calibration: calibrationRef.current, ad_exposures: exposuresRef.current, emotion_analysis_status: 'not_available', aoi_status: 'not_defined' },
        implicit_test: { status: 'completed', paradigm: 'advertisement_attribute_match_rt', deadline_ms: IMPLICIT_DEADLINE_MS, attributes: IMPLICIT_ATTRIBUTES.map((attribute) => attribute.label), score_note: 'Response times and match choices are recorded; a conventional IAT D-score requires contrasted category blocks.' },
      } })
      setPhase('complete')
    } catch (e) { setError('Përgjigjet nuk u ruajtën. Kontrolloni lidhjen me internetin dhe provoni përsëri.'); setPhase('beliefs') }
  }

  const screen = (title, body, action, label) => <main className="mk-screen"><section><div className="mk-kicker">ANKETË NEURO · BREZI Z</div><h1>{title}</h1>{body}{error && <p className="mk-error" role="alert">{error}</p>}{action && <button className="mk-primary" onClick={action}>{label || 'Vazhdo'}</button>}</section></main>
  const yesNo = (name, value, onChange) => <div className="mk-options">{[['yes', 'Po'], ['no', 'Jo']].map(([key, label]) => <label key={key}><input type="radio" name={name} checked={value === key} onChange={() => onChange(key)} />{label}</label>)}</div>

  if (phase === 'consent') return screen('Pëlqimi dhe përgatitja', <><p>Ju lutemi lexoni dhe përgjigjuni përpara se të filloni.</p>{[
    ['age', 'Jam 18 vjeç ose më shumë.'],
    ['camera', 'Jap pëlqimin që kamera e kompjuterit tim të përdoret për gjurmimin e shikimit dhe regjistrimin e fytyrës gjatë reklamave. Videoja nga kamera ruhet për përpunim të mëvonshëm kërkimor, së bashku me koordinatat e nxjerra të shikimit.'],
    ['privacy', 'Pajtohem me politikën e privatësisë dhe mënyrën e trajtimit të të dhënave.'],
  ].map(([key, text]) => <fieldset className="mk-fieldset" key={key}><legend>{text} <b>*</b></legend>{yesNo(key, consent[key], (value) => setConsent({ ...consent, [key]: value }))}</fieldset>)}</>, validateConsent, 'Vazhdo')
  if (phase === 'ineligible') return screen('Faleminderit', <p>Nuk mund të vazhdoni studimin bazuar në përgjigjet tuaja.</p>)
  if (phase === 'camera-check') return <main className="mk-screen mk-camera-check"><section><div className="mk-kicker">ANKETË NEURO · BREZI Z</div><h1>Kontrolli i kamerës</h1><p>Vendoseni fytyrën në qendër të pamjes. Siguroni ndriçim të mirë dhe qëndroni 60–90 cm larg ekranit.</p>{cameraState === 'loading' && <p>Kamera po aktivizohet…</p>}{cameraState === 'ready' && <><p className={faceSeen ? 'mk-face-ok' : 'mk-face-wait'}>{faceSeen ? 'Fytyra juaj u dallua. Mund të vazhdoni.' : 'Po kërkojmë fytyrën tuaj në pamje…'}</p><div className="mk-camera-space" /><button className="mk-primary" disabled={!faceSeen} onClick={startCalibration}>Fillo kalibrimin</button></>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button className="mk-primary" onClick={() => beginCamera(recalibration)}>Provoni përsëri</button></>}</section></main>
  if (phase === 'calibration') return <main className="mk-calibration"><div className="mk-calibration-card"><h1>Konfigurimi dhe kalibrimi</h1><p>Siguroni ndriçim të mirë, shmangni syzet me xhama reflektues, qëndroni 60–90 cm larg ekranit dhe mbajeni kokën të palëvizur.</p>{cameraState === 'loading' && <p>Kamera po aktivizohet…</p>}{cameraState === 'error' && <><p className="mk-error">{error}</p><button onClick={() => beginCamera(recalibration)}>Provoni përsëri</button></>}{cameraState === 'ready' && <p>{faceSeen ? 'Klikoni pesë herë në secilën pikë ndërsa e shikoni.' : 'Vendoseni fytyrën në pamjen e kamerës.'} · {calibrationCounts.reduce((a, b) => a + b, 0)}/{POINTS.length * CLICKS_PER_POINT}</p>}</div>{cameraState === 'ready' && POINTS.map(([x, y], index) => <button key={index} className="mk-point" style={{ left: `${x}%`, top: `${y}%` }} disabled={!faceSeen || calibrationCounts[index] >= CLICKS_PER_POINT} onClick={(event) => clickPoint(index, event)} aria-label={`Pika ${index + 1}`}>{calibrationCounts[index] >= CLICKS_PER_POINT ? '✓' : CLICKS_PER_POINT - calibrationCounts[index]}</button>)}</main>
  if (phase === 'demographics') return screen('Pyetje fillestare dhe të dhëna demografike', <div className="mk-form"><label>Shteti<input value="Kosovë" readOnly /></label><label>Mosha (18–29)<input type="number" min="18" max="29" value={demographics.age} onChange={(e) => setDemographics({ ...demographics, age: e.target.value })} /></label>{[
    ['gender', 'Gjinia', ['Femër', 'Mashkull', 'Jobinare', 'Preferoj të mos përgjigjem']],
    ['population', 'Numri i banorëve në vendbanimin tuaj', ['Më pak se 50.000', '50.000–200.000', '200.000–1.000.000', 'Më shumë se 1.000.000']],
    ['status', 'Statusi', ['Student/e', 'I/E punësuar', 'I/E vetëpunësuar', 'I/E papunë', 'Tjetër']],
  ].map(([key, label, options]) => <label key={key}>{label}<select value={demographics[key]} onChange={(e) => setDemographics({ ...demographics, [key]: e.target.value })}><option value="">Zgjidhni</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>)}</div>, validateDemographics)
  if (phase === 'habits') return screen('Zakonet e përdorimit të mediave', <div className="mk-form"><p>H1. Sa minuta në ditë kaloni në secilën nga platformat e mëposhtme?</p><div className="mk-grid">{PLATFORMS.map((platform) => <label key={platform}>{platform}<input type="number" min="0" max="1440" value={habits.minutes[platform] ?? ''} onChange={(e) => setHabits({ ...habits, minutes: { ...habits.minutes, [platform]: e.target.value } })} /></label>)}</div><Scale questions={['Zakonisht shoh video të shkurtra vertikale (Reels/TikTok).']} answers={{ 0: habits.shortVideos }} onChange={(_, value) => setHabits({ ...habits, shortVideos: value })} prefix="H2" /><fieldset className="mk-fieldset"><legend>H3. Ndjek rregullisht (mund të zgjidhni më shumë se një përgjigje).</legend><div className="mk-checks">{TOPICS.map((topic) => <label key={topic}><input type="checkbox" checked={habits.topics.includes(topic)} onChange={(e) => setHabits({ ...habits, topics: e.target.checked ? [...habits.topics, topic] : habits.topics.filter((item) => item !== topic) })} />{topic}</label>)}</div></fieldset><fieldset className="mk-fieldset"><legend>H4. Gjatë 30 ditëve të fundit kam blerë diçka që e pashë për herë të parë në rrjetet sociale.</legend>{yesNo('socialPurchase', habits.socialPurchase, (value) => setHabits({ ...habits, socialPurchase: value }))}</fieldset><label>H5. Sa për qind të blerjeve tuaja i bëni në internet? <strong>{habits.onlinePercent}%</strong><input type="range" min="0" max="100" value={habits.onlinePercent} onChange={(e) => setHabits({ ...habits, onlinePercent: Number(e.target.value) })} /></label></div>, validateHabits)
  if (phase === 'ad-intro') return screen('Reklamat', <p>Do të shihni {adOrder.length} reklama në rend të rastësishëm. Pas secilës reklamë vjen një test i shkurtër i përputhjes së fjalëve, pastaj disa pyetje. Shikoni ekranin dhe mbajeni kokën të palëvizur.</p>, beginAd, 'Shfaq reklamën')
  if (phase === 'neutral') return <main className="mk-neutral" aria-label="Pauzë prej tre sekondash"><span>+</span></main>
  if (phase === 'ad') return <main className="mk-video"><video ref={videoRef} src={currentAd.url} autoPlay playsInline onPlay={onAdPlay} onEnded={finishAd} onError={() => { faceRecording.abort(); exposureRef.current = null; setError('Reklama nuk mund të ngarkohet. Provoni përsëri.'); setPhase('ad-intro') }} />{!videoStarted && <button className="mk-play" onClick={() => videoRef.current?.play()}>Luaj reklamën</button>}<span>Reklama {adIndex + 1} nga {adOrder.length}</span></main>
  if (phase === 'implicit-intro') return screen('Test i shkurtër i reagimit', <><p>Për secilën fjalë vendosni nëse përputhet me përshtypjen që ju la reklama që sapo patë.</p><p>Përgjigjuni sa më shpejt dhe saktë. Përdorni butonat në ekran ose tastet <strong>E</strong> (nuk përputhet) dhe <strong>I</strong> (përputhet).</p><p>Fjalët lidhen me: <strong>Autentik, Në modë, I besueshëm.</strong></p></>, beginImplicitTest, 'Fillo testin')
  if (phase === 'implicit-fixation' || phase === 'implicit-gap') return <main className="mk-implicit-blank" aria-hidden="true">{phase === 'implicit-fixation' ? '+' : ''}</main>
  if (phase === 'implicit-stimulus') return <main className="mk-implicit"><div className="mk-implicit-progress">{implicitIndex + 1} / {currentImplicitTrials.length}</div><p>A përputhet fjala me reklamën?</p><h1>{currentImplicitTrials[implicitIndex].stimulus}</h1><div className="mk-implicit-actions"><button onClick={() => respondImplicit('does_not_match')}>NUK PËRPUTHET <small>E</small></button><button onClick={() => respondImplicit('matches')}>PËRPUTHET <small>I</small></button></div><div className="mk-implicit-hint">{implicitLate ? 'Përpiquni të përgjigjeni më shpejt për fjalën tjetër.' : 'Përgjigjuni shpejt dhe saktë.'}</div></main>
  if (phase === 'ad-questions') return screen(`Pyetjet pas reklamës ${adIndex + 1}`, <><Scale questions={AD_QUESTIONS} answers={adAnswers.scales} onChange={(index, value) => setAdAnswers({ ...adAnswers, scales: { ...adAnswers.scales, [index]: value } })} prefix="A" /><fieldset className="mk-fieldset"><legend>A7. Do ta ndaja këtë reklamë me miqtë e mi.</legend>{yesNo('share', adAnswers.share, (value) => setAdAnswers({ ...adAnswers, share: value }))}</fieldset><label className="mk-label">A8. Çfarë ju bëri më shumë përshtypje?<input maxLength="300" value={adAnswers.impression} onChange={(e) => setAdAnswers({ ...adAnswers, impression: e.target.value })} /></label></>, saveAdQuestions)
  if (phase === 'beliefs') return screen('Bindjet dhe qëndrimet', <><Scale questions={BELIEF_QUESTIONS} answers={beliefs.scales} onChange={(index, value) => setBeliefs({ ...beliefs, scales: { ...beliefs.scales, [index]: value } })} prefix="B" /><h2>B5. Renditini burimet e informacionit sipas besimit</h2><p>Burimin të cilit i besoni më shumë vendoseni të parin.</p><ol className="mk-ranking">{beliefs.ranking.map((source, index) => <li key={source}>{source}<span><button type="button" onClick={() => moveRanking(index, -1)} disabled={index === 0} aria-label={`${source} lart`}>↑</button><button type="button" onClick={() => moveRanking(index, 1)} disabled={index === beliefs.ranking.length - 1} aria-label={`${source} poshtë`}>↓</button></span></li>)}</ol></>, save, 'Përfundo')
  if (phase === 'recording-saving') return screen('Ruajtja e regjistrimit të kamerës', <p>Ju lutemi prisni…</p>)
  if (phase === 'saving') return screen('Ruajtja e përgjigjeve', <p>Ju lutemi prisni…</p>)
  return screen('Faleminderit', <p>Përgjigjet tuaja u ruajtën.</p>)
}
